"use client";
import cloneDeep from 'lodash/cloneDeep';
import get from 'lodash/get';
import has from 'lodash/has';
import isEmpty from 'lodash/isEmpty';
import merge from 'lodash/merge';
import set from 'lodash/set';
import unset from 'lodash/unset';
import { useId, useRef, useState, useCallback, useEffect } from 'react';
import { useForm, useFieldArray } from 'react-hook-form';
import { useAppPlatformContextOptions } from './AppContextFactory2.js';
import { useErrorsResponseTransform } from './useErrorsResponseTransform.js';
import useValidationResolver from './useReactForm3.js';

const useReactForm = (options) => {
    const { initialValues, currentValues, onFormSubmit, onFormReset, serverMessageLocalizationKey, validateBehavior, initialErrors, onSubmitThrowErrors, } = options;
    const { locale, translate } = useAppPlatformContextOptions();
    const resolver = useValidationResolver(options, locale, translate);
    const formId = useId();
    const { formState: { isValid, isSubmitting, errors, touchedFields, dirtyFields, isDirty }, reset, watch, setValue, getValues, control, getFieldState, handleSubmit, trigger, setError, } = useForm({
        defaultValues: initialValues,
        resolver,
        values: currentValues,
    });
    const initialized = useRef(false);
    const { getErrorMessages } = useErrorsResponseTransform(serverMessageLocalizationKey, true);
    const [additionalErrors, setAdditionalErrorsState] = useState({});
    const additionalErrorsRef = useRef({});
    const isValidForm = () => isValid && isEmpty(additionalErrorsRef.current);
    const setAdditionalErrors = (nextState) => {
        // using ref with state is need to handle cases like setFieldError and submitForm in the same function to not waite state render
        if (typeof nextState === 'function') {
            additionalErrorsRef.current = nextState(additionalErrorsRef.current);
            setAdditionalErrorsState(nextState);
        }
        else {
            additionalErrorsRef.current = nextState;
            setAdditionalErrorsState(nextState);
        }
    };
    const onSubmitForm = async () => {
        let errors = undefined;
        await handleSubmit(async (formValues) => {
            //in case additional errors are visible
            if (!isEmpty(additionalErrorsRef.current))
                return;
            const values = cloneDeep(formValues);
            try {
                await (onFormSubmit === null || onFormSubmit === void 0 ? void 0 : onFormSubmit(values));
            }
            catch (ex) {
                if (onSubmitThrowErrors) {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    errors = ex;
                }
                else {
                    errors = getErrorMessages(ex);
                    errors.map((error) => {
                        const fieldPath = error.field;
                        if (error.field) {
                            setValue(fieldPath, getValues(fieldPath), { shouldTouch: true });
                            onSetFieldError(fieldPath, error.message);
                        }
                    });
                }
            }
        }, (fieldErrors) => {
            if (onSubmitThrowErrors) {
                errors = fieldErrors;
            }
            Object.keys(fieldErrors).forEach((key) => {
                onFieldTouchedDeep(key);
            });
        })();
        if (errors)
            throw errors;
    };
    const onResetForm = async (defaultValues) => {
        setAdditionalErrors({});
        reset(defaultValues !== null && defaultValues !== void 0 ? defaultValues : initialValues);
        await (onFormReset === null || onFormReset === void 0 ? void 0 : onFormReset());
    };
    const onResetTouchedField = (field) => {
        const isTouched = getFieldState(field).isTouched;
        if (isTouched) {
            unset(control._formState.touchedFields, field);
            control._updateFormState(control._formState);
        }
    };
    const onSetFieldValue = useCallback((field, value) => {
        onResetTouchedField(field);
        if (has(additionalErrorsRef.current, field))
            onSetFieldError(field, undefined);
        const hasError = get(additionalErrors, field);
        if (hasError) {
            setAdditionalErrors((prev) => {
                unset(prev, field);
                return prev;
            });
        }
        setValue(field, value, {
            shouldDirty: true,
            shouldTouch: false,
            shouldValidate: true,
        });
    }, [setValue]);
    const onSetFieldsValue = (values) => {
        const keys = Object.keys(values);
        keys.forEach((key) => {
            onSetFieldValue(key, values[key]);
        });
    };
    const onFieldTouched = (field) => {
        const fieldValue = getValues(field);
        setValue(field, fieldValue, {
            shouldTouch: true,
        });
    };
    const onFieldTouchedDeep = (startPath) => {
        const fieldErrors = onGetFieldState(startPath).error;
        const errorKeys = typeof fieldErrors === 'object' ? Object.keys(fieldErrors) : [];
        const hasInnerObjects = errorKeys.some((errorValueKey) => {
            return typeof (fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors[errorValueKey]) === 'object';
        });
        if (!hasInnerObjects) {
            onFieldTouched(startPath);
            return;
        }
        errorKeys.forEach((errorKey) => {
            const errorsValue = fieldErrors === null || fieldErrors === void 0 ? void 0 : fieldErrors[errorKey];
            if (Array.isArray(errorsValue)) {
                errorsValue.forEach((_, index) => {
                    onFieldTouchedDeep(`${startPath}.${errorKey}.${index}`);
                });
            }
            else {
                onFieldTouchedDeep(`${startPath}.${errorKey}`);
            }
        });
    };
    const onGetFieldState = (field) => {
        return getFieldState(field);
    };
    const useReactFormFieldArray = (field) => {
        return useFieldArray({ name: field, control });
    };
    const onSetFieldError = useCallback((field, error) => {
        if (error)
            onFieldTouched(field);
        setAdditionalErrors((prev) => {
            if (error) {
                set(prev, field, typeof error === 'object' ? error : { type: 'CustomError', message: error });
            }
            else {
                let tempField = field;
                unset(prev, tempField);
                do {
                    tempField = tempField.substring(0, tempField.lastIndexOf('.'));
                    if (!isEmpty(get(prev, tempField))) {
                        break;
                    }
                    unset(prev, tempField);
                } while (tempField.includes('.'));
            }
            return prev;
        });
        if (error)
            trigger(field);
        else
            setError(field, { message: undefined });
    }, [setError]);
    const onValidateField = async (field) => {
        await trigger(field);
        const { error } = getFieldState(field);
        return error;
    };
    if (!initialized.current) {
        initialized.current = true;
        if (initialErrors) {
            Object.keys(initialErrors).map((k) => {
                onSetFieldError(k, initialErrors[k]);
            });
        }
    }
    useEffect(() => {
        trigger();
    }, [translate]);
    return {
        disableSubmit: !isValidForm(),
        fieldsErrors: merge(errors, additionalErrors),
        isSubmitting,
        submitForm: onSubmitForm,
        resetForm: onResetForm,
        setFieldValue: onSetFieldValue,
        touchedFields,
        dirtyFields,
        watchValues: watch,
        getValues,
        setFieldTouched: onFieldTouched,
        setFieldTouchedDeep: onFieldTouchedDeep,
        getFieldState: onGetFieldState,
        useFieldArray: useReactFormFieldArray,
        setFieldError: onSetFieldError,
        validateField: onValidateField,
        resetTouchedField: onResetTouchedField,
        isDirty,
        setFieldsValue: onSetFieldsValue,
        validateBehavior,
        formId,
    };
};

export { useReactForm };
